import React, { useEffect, useState, useRef } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Fade from '@mui/material/Fade';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ChevronRight from '@mui/icons-material/ChevronRight';

function sign(px, py, qx, qy, rx, ry) {
	return (px - rx) * (qy - ry) - (qx - rx) * (py - ry);
}

// This function checks if a point (x, y) is inside a triangle formed by three points (x1, y1), (x2, y2), and (x3, y3).
function pointInTriangle(currentMouseCoordinates, triangleCoordinates) {
	const [[x1, y1], [x2, y2], [x3, y3]] = triangleCoordinates;
	const [x, y] = currentMouseCoordinates;

	const b1 = sign(x, y, x1, y1, x2, y2) <= 0;
	const b2 = sign(x, y, x2, y2, x3, y3) <= 0;
	const b3 = sign(x, y, x3, y3, x1, y1) <= 0;
	// If all signs are the same (either all negative or all positive), the point is inside the triangle.
	return b1 === b2 && b2 === b3;
}

const WorklistContextSubmenu = ({ options, menuLevels, onOptionClick, points, transformOrigin }) => {
	const [anchors, setAnchors] = useState({
		elements: new Array(menuLevels).fill(null),
		options: new Array(menuLevels).fill(null),
	});

	const [anchorPositionOffset, setAnchorPositionOffset] = useState({
		top: 40,
		left: 0,
	});

	const mouseEntered = useRef({});
	const mouseLeftCordinates = useRef([]);
	const buttonRef = useRef(null);
	const mouseIdleTimer = useRef(null);

	useEffect(() => {
		if (points?.x !== 0 && points?.y !== 0) {
			setAnchors(prevAnchors => ({
				elements: prevAnchors.elements.map((element, index) => (index === 0 ? points : element)),
				options: prevAnchors.options.map((element, index) => (index === 0 ? options : element)),
			}));
		}
	}, [points]);

	useEffect(() => {
		const newAnchorPositionOffset = {
			top: 50,
			left: 0,
		};

		if (transformOrigin.vertical === 'bottom') {
			newAnchorPositionOffset.top = -80 - 8 - 32 * options.length;
		}
		if (transformOrigin.horizontal === 'right') {
			newAnchorPositionOffset.left = -300;
		}

		setAnchorPositionOffset(newAnchorPositionOffset);
	}, [transformOrigin, options]);

	const handleOpen = (event, level = 0, nestedOptions = options) => {
		const { target } = event;

		setAnchors(prevAnchors => {
			const tempAnchor = {
				elements: prevAnchors.elements.map((element, index) => (index === level ? target : element)),
				options: prevAnchors.options.map((element, index) => (index === level ? nestedOptions : element)),
			};
			return tempAnchor;
		});
	};

	const handleClose = level => {
		setAnchors(prevAnchors => ({
			elements: prevAnchors.elements.map((element, index) => (index >= level ? null : element)),
			options: prevAnchors.options.map((element, index) => (index >= level ? null : element)),
		}));
	};

	const handleClickAway = event => {
		if (event.target === buttonRef.current) {
			handleClose(0);
			return;
		}

		const optionWithoutSubMenu = anchors.elements.every(element => !event.composedPath().includes(element));

		if (optionWithoutSubMenu) {
			handleClose(0);
		}
	};

	const handleClickOption = option => {
		if (!option.nestedOptions) {
			handleClose(0);
		}
		onOptionClick(option);
	};

	const handleMouseMove = (event, option, optIndex) => {
		let shouldComputeSubMenuOpenLogic = true;
		const submenu = document.querySelector(`#nested-menu-${option.menuLevel + 1}`);

		function computeSubMenuLogic() {
			if (!mouseEntered.current[getId(option, optIndex)]) {
				mouseEntered.current[getId(option, optIndex)] = true;
				// Close all prior submenus if the mouse transitions from an option with a submenu to an option without a submenu.
				if (!option.nestedOptions) {
					handleClose(option.menuLevel + 1);
				} else if (
					// If the mouse moves from an option with a submenu to another option with a submenu, open the submenu of the current option and close the submenu of the previous option.
					option.nestedOptions &&
					anchors.options[option.menuLevel + 1] &&
					!option.nestedOptions.every(
						(val, i) => val.value === anchors.options[option.menuLevel + 1]?.[i].value
					)
				) {
					handleClose(option.menuLevel + 1);
					handleOpen(event, option.menuLevel + 1, option.nestedOptions);
				} else {
					handleOpen(event, option.menuLevel + 1, option.nestedOptions);
				}
			}
		}

		if (mouseLeftCordinates.current.length > 0 && submenu) {
			const { x, y, height } = submenu.getBoundingClientRect();
			// Form a virtual triangle using the left mouse coordinates and the top-left and bottom-left coordinates of the submenu. If the current mouse coordinates fall within this triangle, skip the submenu logic computation.
			// Check https://twitter.com/diegohaz/status/1283558204178407427 for more context.
			const currentMouseCoordinates = [event.clientX, -event.clientY];
			const virtualTriangleCordinates = [
				[x, -y],
				[x, -(y + height)],
				[mouseLeftCordinates.current[0], mouseLeftCordinates.current[1]],
			];

			if (pointInTriangle(currentMouseCoordinates, virtualTriangleCordinates)) {
				shouldComputeSubMenuOpenLogic = false;
				if (mouseIdleTimer.current) {
					clearTimeout(mouseIdleTimer.current);
				}

				// if mouse is inside triangle and yet hasn't moved, we need to compute submenu logic after a delay
				mouseIdleTimer.current = setTimeout(() => {
					computeSubMenuLogic();
				}, 50);
			} else {
				shouldComputeSubMenuOpenLogic = true;
			}
		}

		if (shouldComputeSubMenuOpenLogic) {
			if (mouseIdleTimer.current) {
				clearTimeout(mouseIdleTimer.current);
			}
			computeSubMenuLogic();
		}
	};

	const handleMouseLeave = (event, option, optIndex) => {
		mouseLeftCordinates.current = [event.clientX, -event.clientY];

		if (mouseIdleTimer.current) {
			clearInterval(mouseIdleTimer.current);
		}
		mouseEntered.current[getId(option, optIndex)] = false;
	};

	const handleKeyDown = (event, option) => {
		if (option.nestedOptions) {
			if (event.key === 'ArrowRight' || event.key === 'Enter') {
				handleOpen(event, option.menuLevel + 1, option.nestedOptions);
			}
		}
		if (event.key === 'ArrowLeft' && option.menuLevel > 0) {
			handleClose(option.menuLevel);
			anchors.elements[option.menuLevel]?.focus();
		}

		if (event.key === 'Escape') {
			handleClose(0);
		}
	};

	const getId = (option, index) => `${index}-${option.menuLevel}`;

	return (
		<>
			{anchors.elements.map((anchorElement, index) => {
				let anchorEl = anchorElement;
				let anchorRef = 'anchorEl';
				if (anchorElement && anchorElement.x !== 0 && anchorElement.y !== 0 && index === 0) {
					anchorEl = undefined;
					anchorRef = 'anchorPosition';
				}

				return anchorElement ? (
					<Popper
						key={`${anchorElement.innerText} menu`}
						transition
						anchorEl={anchorEl}
						id={`worklist-context-popper${index}`}
						open={(anchorElement.x !== 0 && anchorElement.y !== 0) || Boolean(anchorElement)}
						placement={index > 0 ? 'right-start' : 'bottom-start'}
						style={
							anchorRef === 'anchorPosition' && index === 0
								? {
										position: 'fixed',
										zIndex: 1500,
										width: '300px',
										top: anchorElement.y + anchorPositionOffset.top,
										left: anchorElement.x + anchorPositionOffset.left,
								  }
								: { zIndex: 1510, width: '300px' }
						}
					>
						{({ TransitionProps }) => (
							<Fade {...TransitionProps}>
								<Paper>
									<ClickAwayListener onClickAway={handleClickAway}>
										<MenuList
											aria-labelledby="nested-button"
											autoFocusItem={false}
											id={`nested-menu-${index}`}
										>
											{(anchors.options[index] ?? []).map((option, optIndex) =>
												option.value === 'divider' ? (
													<Divider orientation="horizontal" />
												) : (
													<MenuItem
														key={option.value}
														aria-expanded={
															option.nestedOptions
																? anchors.elements.some(
																		element => element?.innerText === option.value
																  )
																: undefined
														}
														aria-haspopup={!!option.nestedOptions ?? undefined}
														data-testid={`menu-item-${option.value}-${index}`}
														onClick={() => handleClickOption(option)}
														onKeyDown={event => handleKeyDown(event, option)}
														onMouseLeave={event =>
															handleMouseLeave(event, option, optIndex)
														}
														onMouseMove={event => {
															handleMouseMove(event, option, optIndex);
														}}
													>
														<Box
															sx={{
																display: 'flex',
																justifyContent: 'space-between',
																width: '100%',
																alignItems: 'flex-start',
															}}
															onMouseMove={e => e.stopPropagation()}
														>
															{option?.cell ? (
																<Box
																	sx={{
																		width: '100%',
																		overflow: 'hidden',
																		textOverflow: 'ellipsis',
																	}}
																>
																	{' '}
																	{option.cell()}{' '}
																</Box>
															) : (
																<Box
																	sx={{
																		display: 'flex',
																		justifyContent: 'flex-start',
																		alignItems: 'flex-start',
																		gap: '5px',
																	}}
																>
																	{option.icon}
																	<Typography>{option.value}</Typography>
																</Box>
															)}
															{option.nestedOptions ? (
																<ChevronRight fontSize="small" />
															) : null}
														</Box>
													</MenuItem>
												)
											)}
										</MenuList>
									</ClickAwayListener>
								</Paper>
							</Fade>
						)}
					</Popper>
				) : null;
			})}
		</>
	);
};

export default WorklistContextSubmenu;
